import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, XCircle, RotateCcw, UserPlus, ShieldCheck, ExternalLink, DollarSign } from "lucide-react";

export default function AdminOpsPanel() {
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [expandedDriver, setExpandedDriver] = useState(null);
  const [driverDocs, setDriverDocs] = useState({});
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    try {
      const [p, u, b] = await Promise.all([
        api.get("/admin/drivers/pending"),
        api.get("/admin/users"),
        api.get("/admin/bookings"),
      ]);
      setPendingDrivers(p.data);
      setDrivers((u.data || []).filter((x) => x.role === "driver" && (x.approval_status === "approved" || !x.approval_status)));
      setBookings(b.data || []);
    } catch {}
  };
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, []);

  const toggleDocs = async (driverId) => {
    if (expandedDriver === driverId) { setExpandedDriver(null); return; }
    setExpandedDriver(driverId);
    if (!driverDocs[driverId]) {
      try {
        const { data } = await api.get(`/admin/drivers/${driverId}/documents`);
        setDriverDocs((d) => ({ ...d, [driverId]: data }));
      } catch {}
    }
  };

  const approve = async (driverId, ok) => {
    setBusyId(driverId);
    try {
      await api.post(`/admin/drivers/${driverId}/approval`, { approve: ok, note });
      setMsg(ok ? "Driver approved." : "Driver rejected.");
      setNote("");
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed");
    } finally {
      setBusyId("");
      setTimeout(() => setMsg(""), 4000);
    }
  };

  const manualAssign = async (bookingId, driverId) => {
    if (!driverId) return;
    setBusyId(bookingId);
    try {
      await api.post(`/admin/bookings/${bookingId}/assign`, { driver_id: driverId });
      setMsg("Assigned.");
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed");
    } finally {
      setBusyId("");
      setTimeout(() => setMsg(""), 4000);
    }
  };

  const refund = async (bookingId) => {
    if (!window.confirm("Record a refund for this booking? You'll still need to process the actual money-back in the Stripe dashboard.")) return;
    setBusyId(bookingId);
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/refund`, { reason: "Admin refund" });
      setMsg(data.note || "Refund recorded");
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed");
    } finally {
      setBusyId("");
      setTimeout(() => setMsg(""), 5000);
    }
  };

  const unassignedPending = bookings.filter((b) => b.status === "pending" && !b.driver_id);
  const paidBookings = bookings.filter((b) => b.payment_status === "paid").slice(0, 15);

  return (
    <div className="space-y-6" data-testid="admin-ops-panel">
      {msg && <div className="bg-emerald-50 border-2 border-emerald-500 text-emerald-800 px-4 py-2 font-bold text-sm" data-testid="ops-msg">{msg}</div>}

      {/* Driver approval queue */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="text-asap-orange" size={20}/>
          <h3 className="font-display uppercase text-asap-deep">Driver Approval Queue ({pendingDrivers.length})</h3>
        </div>
        {pendingDrivers.length === 0 ? (
          <div className="text-sm text-slate-500">No pending drivers. Every driver is approved.</div>
        ) : (
          <div className="space-y-2" data-testid="pending-drivers-list">
            {pendingDrivers.map((d) => (
              <div key={d.id} className="border-2 border-slate-200 p-4" data-testid={`pending-driver-${d.id}`}>
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div>
                    <div className="font-bold text-asap-deep">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.email} · {d.phone || "no phone"}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest font-bold text-asap-orange">Status: {d.approval_status}</div>
                    <div className="text-[10px] text-slate-400">Docs submitted: {d.docs_submitted}</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => toggleDocs(d.id)} data-testid={`docs-toggle-${d.id}`} className="px-3 py-1.5 border-2 border-asap-deep text-asap-deep font-bold text-xs uppercase tracking-wider hover:bg-asap-deep hover:text-white">
                      {expandedDriver === d.id ? "Hide" : "View"} Docs
                    </button>
                    <button onClick={() => approve(d.id, true)} disabled={busyId === d.id} data-testid={`approve-${d.id}`} className="px-3 py-1.5 bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50">
                      <CheckCircle2 size={12}/> Approve
                    </button>
                    <button onClick={() => approve(d.id, false)} disabled={busyId === d.id} data-testid={`reject-${d.id}`} className="px-3 py-1.5 bg-asap-red text-white font-bold text-xs uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50">
                      <XCircle size={12}/> Reject
                    </button>
                  </div>
                </div>
                {expandedDriver === d.id && (
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                    {(driverDocs[d.id] || []).length === 0 ? (
                      <div className="text-xs text-slate-500">No documents uploaded yet.</div>
                    ) : (
                      (driverDocs[d.id] || []).map((doc) => (
                        <div key={doc.id} className="flex items-center gap-2 text-xs">
                          <span className="inline-block px-2 py-0.5 bg-asap-deep text-white uppercase tracking-widest font-bold text-[9px]">{doc.doc_type}</span>
                          <span className="text-slate-600">{doc.status}</span>
                          <a href={doc.doc_url} target="_blank" rel="noreferrer" className="ml-auto text-asap-orange font-bold inline-flex items-center gap-1"><ExternalLink size={10}/> Open</a>
                        </div>
                      ))
                    )}
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (e.g. rejection reason)" className="w-full px-3 py-2 border-2 border-slate-300 text-xs" data-testid={`note-${d.id}`}/>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Manual dispatch */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="text-asap-orange" size={20}/>
          <h3 className="font-display uppercase text-asap-deep">Manual Dispatch — Unclaimed Requests ({unassignedPending.length})</h3>
        </div>
        {unassignedPending.length === 0 ? (
          <div className="text-sm text-slate-500">No unassigned pending requests. Auto-dispatch is handling it all.</div>
        ) : (
          <div className="space-y-2" data-testid="unassigned-list">
            {unassignedPending.map((b) => (
              <div key={b.id} className="border-2 border-amber-400 bg-amber-50 p-3 flex justify-between items-center gap-3 flex-wrap" data-testid={`unassigned-${b.id}`}>
                <div>
                  <div className="font-bold text-asap-deep">{b.service_name} · ${b.price?.toFixed?.(2)}</div>
                  <div className="text-xs text-slate-600">{b.pickup_address}</div>
                  <div className="text-[10px] text-slate-500">Placed {b.created_at && new Date(b.created_at).toLocaleTimeString()}</div>
                </div>
                <div className="flex gap-2 items-center">
                  <select
                    data-testid={`assign-select-${b.id}`}
                    className="text-xs px-2 py-1.5 border-2 border-asap-deep bg-white"
                    onChange={(e) => manualAssign(b.id, e.target.value)}
                    disabled={busyId === b.id}
                    defaultValue=""
                  >
                    <option value="">— Assign to driver —</option>
                    {drivers.filter((d) => (d.service_capabilities || []).includes(b.service_id)).map((d) => (
                      <option key={d.id} value={d.id}>{d.name} · {(d.service_capabilities || []).join("/")}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Refunds */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <RotateCcw className="text-asap-orange" size={20}/>
          <h3 className="font-display uppercase text-asap-deep">Refunds — Recent Paid Bookings</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="refund-table">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
              <tr><th className="py-2">Booking</th><th>Service</th><th>Customer</th><th>Amount</th><th>Payment</th><th></th></tr>
            </thead>
            <tbody>
              {paidBookings.map((b) => (
                <tr key={b.id} className="border-b last:border-0" data-testid={`refund-row-${b.id}`}>
                  <td className="py-2 text-xs text-slate-500">{b.id.slice(0, 8)}</td>
                  <td className="text-asap-deep font-bold">{b.service_name}</td>
                  <td className="text-slate-600">{b.customer_name}</td>
                  <td className="font-display text-asap-orange">${(b.total || b.price)?.toFixed?.(2)}</td>
                  <td>
                    <span className={`text-[10px] uppercase px-2 py-0.5 border font-bold ${b.payment_status === "refunded" ? "bg-slate-100 text-slate-500 border-slate-300" : "bg-emerald-100 text-emerald-700 border-emerald-500"}`}>{b.payment_status}</span>
                  </td>
                  <td>
                    {b.payment_status === "paid" && (
                      <button onClick={() => refund(b.id)} disabled={busyId === b.id} data-testid={`refund-btn-${b.id}`} className="px-2 py-1 bg-asap-red text-white text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1 disabled:opacity-50">
                        <DollarSign size={10}/> Refund
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {paidBookings.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-slate-400 text-xs">No paid bookings yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-3 p-3 bg-amber-50 border border-amber-400 text-xs text-amber-800">
          <strong>Note:</strong> Refunds are recorded here and marked in the app. To send the money back, complete the refund in your <a href="https://dashboard.stripe.com/payments" target="_blank" rel="noreferrer" className="underline font-bold">Stripe Dashboard → Payments</a>.
        </div>
      </section>
    </div>
  );
}
